import {
  Button,
  Card,
  Inline,
  Stack,
  useViewport,
} from '@omnipresentgroup/design-system';
import { Form } from '@omnipresentgroup/flexible-forms';
import type { IChangeEvent } from '@rjsf/core';
import { useState } from 'react';

import { useManagerUser } from '../../../../hooks';
import { WrappedMixpanel } from '../../../../mixpanelWrapper';
import { useAddContractorSchema } from '../../hooks';
import type { AddContractorFields, AddContractorFormFields } from '../../types';
import type { StepComponentProps } from '../Stepper';
import { AddContractorFormSkeleton } from './AddContractorFormSkeleton';
import { CompliantContractCard } from './CompliantContractCard';

const FORM_ID = 'add-contractor-form';

type Props = StepComponentProps<AddContractorFields> & {
  companyId: string;
};

export const AddContractorForm = ({ companyId, data, back, next }: Props) => {
  const viewport = useViewport();

  const [formValues, setFormValues] = useState<
    AddContractorFormFields | undefined
  >(data?.contractor);

  const { userId } = useManagerUser();

  const {
    schema: flexibleSchema,
    uiSchema: flexibleUiSchema,
    isLoading: isLoadingSchema,
  } = useAddContractorSchema({
    formValues,
  });

  // Form Change
  const handleChange = ({
    formData,
  }: IChangeEvent<AddContractorFormFields>) => {
    setFormValues(formData);
  };

  // Form Submit
  const handleSubmit = () => {
    if (formValues) {
      next?.handleClick?.({ contractor: formValues });
    }
    WrappedMixpanel.track('Add Contractor Next Clicked', {
      clientId: companyId,
      userId: userId,
    });
  };

  return (
    <Card maxW={600} p={viewport.isPhone ? '24' : '32'} w="stretch">
      {isLoadingSchema ? (
        <AddContractorFormSkeleton />
      ) : (
        <Stack gap="32">
          <Stack gap="16">
            <Form<AddContractorFormFields>
              id={FORM_ID}
              schema={flexibleSchema}
              uiSchema={flexibleUiSchema}
              formData={formValues}
              onChange={handleChange}
              onSubmit={handleSubmit}
            />
            <CompliantContractCard companyId={companyId} />
          </Stack>
          <Inline justify="space-between">
            <Button
              variant="secondary"
              onClick={() => {
                back?.handleClick?.({ contractor: formValues });
              }}
              disabled={back?.disabled}
              loading={back?.loading}
            >
              Back
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              disabled={next?.disabled}
              loading={next?.loading}
            >
              Next
            </Button>
          </Inline>
        </Stack>
      )}
    </Card>
  );
};
